
import React from 'react';
import { FieldRenderProps, UseFieldConfig } from 'react-final-form';

import { TextInput, TextInputProps } from '@instructure/ui-text-input'
import { NumberInput, NumberInputProps } from '@instructure/ui-number-input'
import { Select, SelectProps } from '@instructure/ui-select'
import { Checkbox } from '@instructure/ui-checkbox'
import { TextArea, TextAreaProps } from '@instructure/ui-text-area'
import { RadioInputGroup } from '@instructure/ui-radio-input'

export type FFAdapterComponent = React.ComponentType<FieldRenderProps<any>>;

type Unpick<T, K extends keyof T> = {
    [P in keyof T]: P extends K ? never : T[P];
};

/**
 * A better FieldProps type for React Final Form.
 * 
 * TODO: An Omit approach (to omit FF injected props like `input` and `meta`) below would be better,
 * but appears to be bugged in TS - whenever used, the expected type is not generated
 */
export type FieldComponentProps<C extends FFAdapterComponent> =
    UseFieldConfig<any> &
    // React.ComponentProps<C> &
    Partial<React.ComponentProps<C>> &
    // Omit<React.ComponentProps<C>, 'input'> &
    // Unpick<React.ComponentProps<C>, 'input'> &
    { component?: C, name: string };

export function errorsToList(errs) {
    if (typeof errs == 'string') {
        return errs.split("\n")
    }
    if (!errs) return [];
    if (!Array.isArray(errs)) return [errs];
    return errs;
}

export const rest_of_messages = Symbol('Rest of Messages');
type PropMessages = ((fieldMessages: any[]) => any[]) | any[];
export const adaptMessages = (meta: FieldRenderProps<any>['meta'], propMessages: PropMessages = [rest_of_messages]) => {
    let messages: TextInputProps['messages'] = [];
    const errs = [...errorsToList(meta.error)];

    if (!meta.dirtySinceLastSubmit) {
        for (let e of errorsToList(meta.submitError)) {
            errs.push(e);
        }
    }

    if (meta.touched && !meta.active) {
        for (let err of errs) {
            messages.push({
                type: 'error',
                text: err,
            })
        }
    }

    if (typeof propMessages == 'function') {
        return propMessages(messages);
    } else {
        const mapped_messages = []
        for (let m of propMessages) {
            if (m == rest_of_messages) {
                mapped_messages.push(...messages);
            } else {
                mapped_messages.push(m);
            }
        }
        return mapped_messages;
    }
}

interface BufferMetaProps {
    changeKey?: string,
    valueKey?: string,
    blurKey?: string,
    bufferedValue: (...args) => any;
}

export const BufferedInput = <T extends React.ComponentType>(props: { Component: T, } & BufferMetaProps & React.ComponentProps<T>) => {
    const {
        Component,
        changeKey = 'onChange',
        blurKey = 'onBlur',
        valueKey = 'value',
        bufferedValue = null,
        ...rest
    } = props;
    const [bufferVal, setBuffered] = React.useState();

    const compProps = {
        [valueKey]: bufferVal !== undefined ? bufferVal : props[valueKey],
        [changeKey]: (...args) => {
            setBuffered(bufferedValue(...args));
            props[changeKey] && props[changeKey](...args);
        },
        [blurKey]: (...args) => {
            setBuffered(undefined);
            props[blurKey] && props[blurKey](...args);
        },
    }

    // @ts-ignore
    return <Component
        {...rest}
        {...compProps}
    />
}

export const makeBuffered = <T extends React.ComponentType>(component: T, props: BufferMetaProps) => {
    const buffered: React.SFC<React.ComponentProps<T>> = ({ ...rest }) => {
        return BufferedInput({
            Component: component,
            ...props,
            ...rest,
        })
    }
    return buffered;
}

interface AdapterMetaProps<T extends React.ComponentType> {
    changeKey?: string,
    valueKey?: string,
    valueMap?: (value: any) => any;
    props?: Partial<React.ComponentProps<T>>
}

export const FieldAdapter = <T extends React.ComponentType>(props: FieldRenderProps<any> & React.ComponentProps<T> & AdapterMetaProps<T> & {
    ui_component: React.ComponentType,
    onChange?: (...args) => any;
}) => {
    const {
        ui_component: Component,
        changeKey = 'onChange',
        valueKey = 'value',
        onChange = (evt) => evt.currentTarget.value,
        props: extraProps,

        valueMap = (v) => v,

        input,
        meta,
        ...rest
    } = props;

    for (let [k, v] of Object.entries(rest)) {
        if (k[0] == '_') {
            delete rest[k];
            rest[k.slice(1) as any] = v;
        }
    }

    const { value: fieldValue, onChange: updateFieldValue, ...passInput } = input;

    const compProps = {
        [valueKey]: valueMap(fieldValue),
        [changeKey]: (...args) => {
            updateFieldValue(onChange(...args));
        },
    }

    // @ts-ignore
    return <Component
        {...passInput}
        {...rest}
        {...extraProps}
        {...compProps}
    />
}

export const makeAdapter = <T extends React.ComponentType>(rootAdapter: typeof FieldAdapter, Component: T, onChange?: (...args) => any, extra: AdapterMetaProps<T> = {}) => {
    const adapter: React.SFC<FieldRenderProps<any> & React.ComponentProps<T>> = ({ input, meta, ...rest }) => {
        return rootAdapter<T>({
            ui_component: Component,
            onChange,
            input,
            meta,
            ...rest,
            ...extra as any,
        })
    }
    return adapter;
}

export const InstUIAdapter: typeof FieldAdapter = (props) => {
    const { meta } = props;
    const messages = adaptMessages(meta, props.messages);
    return <FieldAdapter messages={messages} {...props} />
}

export const makeInstUIAdapter = <T extends React.ComponentType<any>>(Component: T, onChange?: (...args) => any, extra: AdapterMetaProps<T> = {}) => {
    return makeAdapter(InstUIAdapter, Component, onChange, extra);
}

export const makeSelectAdapter = <T extends React.ComponentType<any>>(Component: T, onChange?: (option) => any) => {
    return makeInstUIAdapter(Component, (event, opt) => {
        return opt && opt.value != '---' ? (onChange ? onChange(opt) : opt.value) : null;
    }, {
        valueKey: 'selectedOption',
        valueMap: (v) => (v == null || v == '') ? '---' : v,
    });
};

export const TextInputAdapter: React.SFC<FieldRenderProps<any> & TextInputProps> = ({ input, meta, maxLength, ...rest }) => {
    return <TextInput
        {...input as any}
        {...rest}
        messages={adaptMessages(meta, rest.messages)}
        onChange={(event) => {
            const value = event.currentTarget.value || '';
            if (maxLength && value.length > maxLength) return;
            input.onChange(event.currentTarget.value)
        }}
    />
}

export const BufferedNumberInput = makeBuffered(NumberInput, {
    bufferedValue: (evt, raw: string) => {
        let dcount = 0;
        raw = (raw || '').replace(/[^\d\.,]/g, '').replace(/\.+/g, () => dcount++ == 0 ? '.' : '');
        return (raw == null || raw == parseFloat(raw).toString()) ? undefined : raw;
    },
})

export const GenericAdapter = makeInstUIAdapter(null, v => v);

export const NumberInputAdapter = makeInstUIAdapter(BufferedNumberInput, (evt, value, norm) => norm);

export const SelectAdapter = makeSelectAdapter(Select);

export const CheckBoxAdapter = makeInstUIAdapter(Checkbox, (evt) => evt.target.checked, { valueKey: 'checked', valueMap: (v) => (v == '') ? false : v });

export const RadioGroupAdapter = makeInstUIAdapter(RadioInputGroup, (evt, value) => value);

export const TextAreaAdapter = makeInstUIAdapter(TextArea, (evt, value) => (event.target as any).value);

// Date/DateTime Input Adapter

// import { BaseDateInput, BaseDateTimeInput } from './BaseDateInput';
// export const DateInputAdapter = makeInstUIAdapter(BaseDateInput,
//     (evt, isoValue, rawValue, conversionFailed) => (conversionFailed ? 'INVALID' : isoValue),
//     { valueKey: 'dateValue', changeKey: 'onDateChange' }
// );

// export const DateTimeInputAdapter = makeInstUIAdapter(BaseDateTimeInput,
//     (evt, isoValue, rawValue, conversionFailed) => (conversionFailed ? 'INVALID' : isoValue),
//     { valueKey: 'dateValue', changeKey: 'onDateChange' }
// );

// Code Editor Adapter

// import { CodeEditor } from '@instructure/ui-code-editor'
// export const CodeEditorAdapter: React.SFC<FieldRenderProps<any> & {
//     label: string,
//     language?: string,
//     readOnly?: boolean,
// }> = ({ input, meta, ...rest }) => {
//     const { value, ...inputRest } = input;
//     return <CodeEditor {...inputRest} {...rest} defaultValue={value} />
// }
